import React from 'react';
import { useTranslation } from 'react-i18next';
import { components } from 'react-select';

import { Tag } from 'common/components';
import { SelectStyled } from 'common/components/form/Select/Select.styled';
import { useFormikField } from 'common/components/form/useFormikField';
import { FieldWrapper } from 'common/components/form/Wrapper';
import { ISelectModel } from 'common/models';

interface SelectCheckboxTagProps {
  name: string;
  label?: string;
  options: ISelectModel<any>[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  margin?: string;
  disabledValues?: number[];
}

export const SelectCheckboxTag: React.FC<SelectCheckboxTagProps> = ({
  name,
  label,
  options,
  placeholder,
  disabled,
  required,
  margin,
  disabledValues = [],
}) => {
  const { t } = useTranslation();
  const { value, setValue, error } = useFormikField(name);

  const selectedValues: string[] = Array.isArray(value) ? value : [];

  const toggleValue = (option: ISelectModel<any>) => {
    if (disabledValues.includes(option.value)) return;

    if (selectedValues.includes(option.value)) {
      setValue(selectedValues.filter(v => v !== option.value));
    } else {
      setValue([...selectedValues, option.value]);
    }
  };

  const selectedOptions = options.filter(o => selectedValues.includes(o.value));

  const dynamicPlaceholder =
    selectedOptions.length === 0
      ? placeholder || t('Select')
      : `${selectedOptions.length} ${t('Options')}`;

  const Option = (props: any) => {
    const opt: ISelectModel<any> = props.data;

    const isChecked = selectedValues.includes(opt.value);
    const isDisabled = disabled || disabledValues.includes(opt.value);

    return (
      <components.Option {...props} isDisabled={isDisabled}>
        <div
          onClick={() => !isDisabled && toggleValue(opt)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            padding: '4px 0',
          }}
        >
          <input
            type="checkbox"
            checked={isChecked}
            disabled={isDisabled}
            readOnly
            style={{
              transform: 'scale(1.3)',
              pointerEvents: 'none',
            }}
          />
          <span>{opt.label}</span>
        </div>
      </components.Option>
    );
  };

  return (
    <FieldWrapper
      name={name}
      label={label}
      isRequired={required}
      error={error}
      margin={margin}
      isDisabled={disabled}
    >
      <SelectStyled
        name={name}
        options={options}
        isMulti={false}
        value={null}
        onChange={() => null}
        placeholder={dynamicPlaceholder}
        isDisabled={disabled}
        closeMenuOnSelect={false}
        controlShouldRenderValue={false}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        classNamePrefix="true"
        styles={{
          menuPortal: base => ({ ...base, zIndex: 9999 }),
        }}
        components={{ Option }}
      />

      {selectedOptions.length > 0 && (
        <div
          style={{
            marginTop: '0.5rem',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.25rem',
          }}
        >
          {selectedOptions.map(opt => (
            <Tag
              key={opt.value}
              label={opt.label}
              onRemove={disabledValues.includes(opt.value) ? undefined : () => toggleValue(opt)}
            />
          ))}
        </div>
      )}
    </FieldWrapper>
  );
};
